/**
 * MicrosoftGraphConfigCard (WQ-204)
 *
 * Form for entering Microsoft Graph credentials. Lives on the SSO &
 * Integration page below the SSO providers section. Backed by
 * /api/admin/microsoft-graph/* (built in WQ-066, no UI until now).
 *
 * Pattern matches the existing SSO Edit Provider modal — required-asterisk
 * fields, Save/Cancel/Test/Disable buttons, "leave blank to keep" hint
 * on the secret field.
 */
import React, { useEffect, useState } from 'react';
import {
  type GraphConfig,
  type GraphConfigRequest,
  disableGraph,
  getGraphConfig,
  saveGraphConfig,
  testGraphConnection,
} from '../../services/microsoftGraphService';

const DEFAULT_SCOPES = ['Sites.Read.All', 'Sites.ReadWrite.All'];

interface Props {
  authFetch: (url: string, options?: RequestInit) => Promise<Response>;
  tenantId?: string | null;
  tenantLabel?: string;
}

const MicrosoftGraphConfigCard: React.FC<Props> = ({
  authFetch, tenantId, tenantLabel,
}) => {
  const [config, setConfig] = useState<GraphConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ healthy: boolean; message?: string } | null>(null);

  // Form state
  const [azureTenantId, setAzureTenantId] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [sharepointEnabled, setSharepointEnabled] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getGraphConfig(authFetch, tenantId)
      .then((c) => {
        if (cancelled) return;
        setConfig(c);
        setAzureTenantId(c.azure_tenant_id || '');
        setClientId(c.client_id || '');
        setDisplayName(c.display_name || '');
        // WQ-216: only sync enabled flags from server when a config actually
        // exists. Empty-state response carries enabled=false which would flip
        // the form checkbox to unchecked and cause a silent first-save bug.
        if (c.has_client_secret) {
          setEnabled(c.enabled);
          setSharepointEnabled(c.sharepoint_enabled);
        }
      })
      .catch(() => {
        if (cancelled) return;
        // No config yet — initial state already empty.
        setConfig(null);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [authFetch, tenantId]);

  const handleSave = async () => {
    setError(null);
    setSuccess(null);
    setTestResult(null);
    if (!azureTenantId.trim() || !clientId.trim()) {
      setError('Azure Tenant ID and Client ID are required.');
      return;
    }
    if (!config?.has_client_secret && !clientSecret.trim()) {
      setError('Client Secret is required for the initial save.');
      return;
    }
    const payload: GraphConfigRequest = {
      azure_tenant_id: azureTenantId.trim(),
      client_id: clientId.trim(),
      display_name: displayName.trim() || undefined,
      enabled,
      sharepoint_enabled: sharepointEnabled,
      graph_scopes: DEFAULT_SCOPES,
    };
    if (clientSecret.trim()) payload.client_secret = clientSecret.trim();
    try {
      await saveGraphConfig(authFetch, payload, tenantId);
      setSuccess('Microsoft Graph configuration saved.');
      setClientSecret('');
      const refreshed = await getGraphConfig(authFetch, tenantId);
      setConfig(refreshed);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    }
  };

  const handleTest = async () => {
    setError(null);
    setSuccess(null);
    setTestResult(null);
    const result = await testGraphConnection(authFetch, tenantId);
    setTestResult({ healthy: result.healthy, message: result.message });
  };

  const handleDisable = async () => {
    if (!confirm('Disable Microsoft Graph integration? Background sync will stop until re-enabled.')) {
      return;
    }
    setError(null);
    try {
      await disableGraph(authFetch, tenantId);
      setSuccess('Microsoft Graph integration disabled.');
      setEnabled(false);
      const refreshed = await getGraphConfig(authFetch, tenantId);
      setConfig(refreshed);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Disable failed');
    }
  };

  const scopeLabel = tenantLabel ? `for ${tenantLabel}` : '(platform default)';

  return (
    <div className="card" data-testid="ms-graph-config-card" style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 className="card-title" style={{ margin: 0 }}>
          Microsoft Graph {scopeLabel}
        </h3>
        {config?.enabled && (
          <span className="badge badge-success">Enabled</span>
        )}
      </div>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 8 }}>
        App-level Graph API credentials. Used by background SharePoint sync,
        calendar integrations, and admin Graph queries.{' '}
        <strong>Different from SSO credentials</strong> (those live in the
        Available SSO Identity Providers section above) — see{' '}
        <code>documentation/MICROSOFT_INTEGRATION_ARCHITECTURE.md</code>.
      </p>

      {error && (
        <div className="alert alert-error" data-testid="ms-graph-error">
          {error}
        </div>
      )}
      {success && (
        <div className="alert alert-success" data-testid="ms-graph-success">
          {success}
        </div>
      )}
      {testResult && (
        <div
          className={`alert ${testResult.healthy ? 'alert-success' : 'alert-error'}`}
          data-testid="ms-graph-test-result"
        >
          {testResult.healthy
            ? `Connection healthy: ${testResult.message || 'OK'}`
            : `Test failed: ${testResult.message || 'Unknown error'}`}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 16 }}>
        <div>
          <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
            Azure AD Directory (Tenant) ID *
          </label>
          <input
            type="text"
            value={azureTenantId}
            onChange={(e) => setAzureTenantId(e.target.value)}
            placeholder="00000000-0000-0000-0000-000000000000"
            disabled={loading}
            style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 13 }}
          />
        </div>
        <div>
          <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
            Application (Client) ID *
          </label>
          <input
            type="text"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            placeholder="00000000-0000-0000-0000-000000000000"
            disabled={loading}
            style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 13 }}
          />
        </div>
        <div>
          <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
            Client Secret {config?.has_client_secret ? '(leave blank to keep)' : '*'}
          </label>
          <input
            type="password"
            value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
            placeholder={config?.has_client_secret ? '••••••••••••' : 'Enter client secret'}
            disabled={loading}
            autoComplete="new-password"
            style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 13 }}
          />
        </div>
        <div>
          <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
            Display Name (optional)
          </label>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g., IWT Microsoft Graph"
            disabled={loading}
            style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 13 }}
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            disabled={loading}
          />
          Enabled
        </label>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input
            type="checkbox"
            checked={sharepointEnabled}
            onChange={(e) => setSharepointEnabled(e.target.checked)}
            disabled={loading}
          />
          SharePoint sync enabled
        </label>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button className="btn btn-primary" onClick={handleSave} disabled={loading} data-testid="ms-graph-save">
          Save
        </button>
        <button className="btn btn-secondary" onClick={handleTest} disabled={loading || !config?.has_client_secret} data-testid="ms-graph-test">
          Test Connection
        </button>
        {config?.enabled && (
          <button className="btn btn-ghost" onClick={handleDisable} disabled={loading} data-testid="ms-graph-disable">
            Disable
          </button>
        )}
      </div>
    </div>
  );
};

export default MicrosoftGraphConfigCard;
